import { Inject, Injectable } from '@angular/core';
import {
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
  interfaces,
  PaginationHelper,
} from '@daas-consulting/core-cross-interfaces';
import * as crossUtils from '@daas-consulting/core-cross-utils';
import * as store from '@daas-consulting/core-frontend-store';
import {
  RemitosService,
  RemitosServiceId,
} from '@daas-consulting/production/remitos/frontend/data_contracts';
import * as models from '@daas-consulting/production/remitos/frontend/models';
import {
  errorMessage,
  loader,
} from '@daas-consulting/production/shared/frontend/store';
import { Action, ofActionDispatched, State, StateContext } from '@ngxs/store';
import { catchError, take, tap } from 'rxjs';
import * as actions from './remitos-list.actions';
import * as consts from './remitos-list.const';
import { cases } from './remitos-list.defaults';
import { Filter, RemitosListStateModel } from './remitos-list.state-model';

export const defaultFilter: Filter = {
  printNumber: null,
  status: null,
  date_from: null,
  date_to: null,
};

export const defaults: RemitosListStateModel = {
  pagedList: {
    items: [],
    orderBy: [],
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
    totalItemCount: 0,
  },

  requestPageIndex: DEFAULT_PAGE_INDEX,
  activePageIndex: DEFAULT_PAGE_INDEX,

  requestPageSize: DEFAULT_PAGE_SIZE,
  activePageSize: DEFAULT_PAGE_SIZE,

  filterForm: {
    model: defaultFilter,
    dirty: false,
    status: store.FormStatusTypes.VALID,
    errors: {},
  },

  requestFilterModel: defaultFilter,
  activeFilterModel: defaultFilter,

  requestFilterCriteria: [],
  activeFilterCriteria: [],

  requestOrderBy: [],
  activeOrderBy: [],

  cancelRequest: null,
};

@State<RemitosListStateModel>({
  name: consts.stateTokenName,
  defaults,
})
@Injectable()
export class RemitosListState {
  readonly #service: RemitosService;
  readonly #eventHelper: store.EventHelper;
  readonly #labels = new Map([
    ['printNumber', 'Número'],
    ['status', 'Estado'],
    ['emissionDate', 'Fecha'],
  ]);

  constructor(
    @Inject(RemitosServiceId) service: RemitosService,
    @Inject(store.EventHelper) eventHelper: store.EventHelper
  ) {
    this.#service = service;
    this.#eventHelper = eventHelper;
    this._connectEvents();
  }

  @Action(actions.LoadItemsRequest)
  loadItemsRequest(ctx: StateContext<RemitosListStateModel>): void {
    const state = ctx.getState();
    const params = this._mapToRequest(state);
    this.#service
      .getAll$(params)
      .pipe(
        take(1),
        tap((data: interfaces.PagedList<models.RemitoListItemModel>) => {
          ctx.dispatch(new actions.LoadItemsSuccessful(data));
        }),
        catchError((error) => {
          ctx.dispatch(new actions.LoadItemsFailure(error as Error));
          return store.handleError(error);
        })
      )
      .subscribe();
  }

  @Action(actions.LoadItemsSuccessful)
  loadItemsSuccessful(
    ctx: StateContext<RemitosListStateModel>,
    { payload }: actions.LoadItemsSuccessful
  ): void {
    const state = ctx.getState();
    const filter = state?.requestFilterModel || defaultFilter;
    const filters: interfaces.Criteria[] = store.getFilterFromModel({
      model: filter,
      labels: this.#labels,
      cases,
    });
    ctx.patchState({
      activeFilterModel: state?.requestFilterModel,
      activeFilterCriteria: filters,
      activePageIndex: payload.pageIndex,
      activePageSize: payload.pageSize,
      pagedList: { ...payload },
    });
  }

  @Action(actions.LoadItemsFailure)
  loadItemsFailure(
    ctx: StateContext<RemitosListStateModel>,
    { payload }: actions.LoadItemsFailure
  ) {
    return ctx.dispatch(new errorMessage.actions.ShowError(payload));
  }

  @Action(actions.SetFilter)
  setFilters(ctx: StateContext<RemitosListStateModel>): void {
    const state = ctx.getState();
    const filter = state.filterForm.model || defaultFilter;
    const criteria = store.getFilterFromModel({
      model: filter,
      labels: this.#labels,
      cases,
    });
    ctx.patchState({
      requestFilterModel: filter,
      requestFilterCriteria: criteria,
    });
  }

  @Action(actions.SetFullFilter)
  setFullFilter(
    ctx: StateContext<RemitosListStateModel>,
    action: actions.SetFullFilter
  ): void {
    const state = ctx.getState();
    let filterAction = undefined;

    if (action?.payload?.filters?.length) {
      const data = action.payload.filters as unknown as Omit<
        interfaces.Criteria,
        'label'
      >[];
      const newFilter = store.getModelFromFilter({
        criterias: data,
        defaultFilter,
      });
      ctx.patchState({ filterForm: { ...state.filterForm, model: newFilter } });
      filterAction = newFilter;
    }

    const filter = filterAction || state?.filterForm?.model || defaultFilter;
    const criteria = store.getFilterFromModel({
      model: filter,
      labels: this.#labels,
      cases,
    });
    const { pageIndex: safePageIndex, pageSize: safePageSize } =
      PaginationHelper.sanitize({
        pageIndex: action?.payload?.pageIndex ?? state.requestPageIndex,
        pageSize: action?.payload?.pageSize ?? state.requestPageSize,
      });
    ctx.patchState({
      requestFilterModel: filter,
      requestFilterCriteria: criteria,
      requestPageIndex: safePageIndex,
      requestPageSize: safePageSize,
    });
  }

  @Action(actions.RemoveFilter)
  removeFilter(
    ctx: StateContext<RemitosListStateModel>,
    { payload }: actions.RemoveFilter
  ): void {
    const state = ctx.getState();
    const model = state.filterForm.model as Filter;
    if (model) {
      const modelKeys = Object.keys(model).filter(
        (key) => key === payload || key.startsWith(`${payload}_`)
      );
      const updatedModel = { ...model };
      modelKeys.forEach((modelKey) => {
        const key = modelKey as keyof Filter;
        updatedModel[key] = null;
      });
      ctx.patchState({
        filterForm: { ...state.filterForm, model: updatedModel },
      });
    }
  }

  @Action(actions.SetPagination)
  setPagination(
    ctx: StateContext<RemitosListStateModel>,
    { payload }: actions.SetPagination
  ): void {
    const state = ctx.getState();
    const pageIndex =
      state.activePageSize === payload.pageSize ? payload.pageIndex : 0;
    ctx.patchState({
      requestPageIndex: pageIndex,
      requestPageSize: payload.pageSize,
    });
  }

  @Action(actions.ResetPagination)
  resetPagination(ctx: StateContext<RemitosListStateModel>): void {
    ctx.patchState({ requestPageIndex: DEFAULT_PAGE_INDEX });
  }

  @Action(actions.SetOrder)
  setOrder(
    ctx: StateContext<RemitosListStateModel>,
    { payload }: actions.SetOrder
  ): void {
    ctx.patchState({
      requestOrderBy: [payload],
    });
  }

  @Action(actions.SetFiltersInUrl)
  setFiltersInUrl(ctx: StateContext<RemitosListStateModel>): void {
    const state = ctx.getState();
    const data = {
      filters: state.activeFilterCriteria,
      pageIndex: state.activePageIndex,
      pageSize: state.activePageSize,
      order: state.activeOrderBy,
    };
    store.setFiltersInUrl(
      ctx,
      '/remitos/list',
      data,
      store.datetimeToDateFormat
    );
  }

  @Action(actions.CancelItem)
  cancelItem(
    ctx: StateContext<RemitosListStateModel>,
    { payload }: actions.CancelItem
  ): void {
    ctx.patchState({ cancelRequest: payload });
  }

  @Action(actions.CancelItemRequest)
  cancelItemRequest(ctx: StateContext<RemitosListStateModel>): void {
    const state = ctx.getState();
    const { remitoId, canceledReason } = state.cancelRequest!;
    this.#service
      .cancel$(remitoId, canceledReason)
      .pipe(
        take(1),
        tap(() => ctx.dispatch(new actions.CancelItemSuccessful())),
        catchError((error) => {
          ctx.dispatch(new actions.CancelItemFailure(error as Error));
          return store.handleError(error);
        })
      )
      .subscribe();
  }

  @Action(actions.CancelItemSuccessful)
  cancelItemSuccessful(ctx: StateContext<RemitosListStateModel>): void {
    ctx.patchState({ cancelRequest: null });
    ctx.dispatch(new actions.LoadItems());
  }

  @Action(actions.CancelItemFailure)
  cancelItemFailure(
    ctx: StateContext<RemitosListStateModel>,
    { payload }: actions.CancelItemFailure
  ): void {
    ctx.dispatch(new errorMessage.actions.ShowError(payload));
  }

  private _connectEvents(): void {
    this.#eventHelper.connectEvents(actions.RemoveFilter, actions.SetFilter);
    this.#eventHelper.connectEvents(actions.SetFilter, actions.ResetPagination);
    this.#eventHelper.connectEvents(actions.ResetPagination, actions.LoadItems);
    this.#eventHelper.connectEvents(
      actions.LoadItemsSuccessful,
      actions.SetFiltersInUrl
    );
    this.#eventHelper.connectEvents(actions.SetPagination, actions.LoadItems);
    this.#eventHelper.connectEvents(actions.SetFullFilter, actions.LoadItems);
    this.#eventHelper.connectEvents(actions.SetOrder, actions.LoadItems);

    this.#eventHelper.connectEvents(
      actions.LoadItems,
      loader.actions.ShowLoading,
      ofActionDispatched
    );
    this.#eventHelper.connectEvents(
      actions.LoadItems,
      actions.LoadItemsRequest
    );
    this.#eventHelper.connectEvents(
      actions.LoadItemsSuccessful,
      loader.actions.HideLoading,
      ofActionDispatched
    );
    this.#eventHelper.connectEvents(
      actions.LoadItemsFailure,
      loader.actions.HideLoading,
      ofActionDispatched
    );

    this.#eventHelper.connectEvents(
      actions.CancelItem,
      loader.actions.ShowLoading,
      ofActionDispatched
    );
    this.#eventHelper.connectEvents(
      actions.CancelItem,
      actions.CancelItemRequest
    );
    this.#eventHelper.connectEvents(
      actions.CancelItemSuccessful,
      loader.actions.HideLoading,
      ofActionDispatched
    );
    this.#eventHelper.connectEvents(
      actions.CancelItemFailure,
      loader.actions.HideLoading,
      ofActionDispatched
    );
  }

  private _mapToRequest(
    state: RemitosListStateModel
  ): interfaces.CriteriaRequest {
    const orderBy = state.requestOrderBy?.[0];
    return crossUtils.criteria.mapCriteriaToCriteriaRequest({
      criterias: state.requestFilterCriteria || [],
      orderField: orderBy?.field ?? 'emissionDate',
      orderType: orderBy?.type ?? 'desc',
      pageIndex: state.requestPageIndex,
      pageSize: state.requestPageSize,
    });
  }
}
